/* ============================================================
   bd-section-featured-collection.js
   - Desktop + Mobile: carousel + swipe + arrows + dots
   - No gap/empty space issue
   - Wishlist / Quick View / Add to Cart are now handled globally
     by assets/bd-wishlist.js and assets/bd-quick-view.js (event
     delegation on document), so this file only owns the carousel.
   ============================================================ */

(function () {
  'use strict';

  function initCarousel(section) {
    var track     = section.querySelector('[data-fc-track]');
    var trackWrap = section.querySelector('[data-fc-track-wrap]');
    var dotsWrap  = section.querySelector('[data-fc-dots]');
    var prevBtn   = section.querySelector('[data-fc-prev]');
    var nextBtn   = section.querySelector('[data-fc-next]');

    if (!track || !trackWrap) return;

    var cards      = Array.from(track.querySelectorAll('[data-fc-card]'));
    var totalCards = cards.length;
    var currentIndex = 0;

    if (totalCards === 0) return;

    /* ── Card width ── */
    function getCardWidth() {
      var card = cards[0];
      if (!card) return 0;
      var style = window.getComputedStyle(track);
      var gap   = parseFloat(style.gap) || parseFloat(style.columnGap) || 16;
      return card.offsetWidth + gap;
    }

    /* ── Visible count ── */
    function getVisibleCount() {
      var wrapW = trackWrap.offsetWidth;
      var cardW = getCardWidth();
      if (cardW <= 0) return 1;
      return Math.max(1, Math.round(wrapW / cardW));
    }

    /* ── Max scroll index ── */
    function getMaxIndex() {
      return Math.max(0, totalCards - getVisibleCount());
    }

    /* ── Go to index ── */
    function goTo(index, animate) {
      var max = getMaxIndex();
      if (index < 0)   index = 0;
      if (index > max) index = max;
      currentIndex = index;
      track.style.transition = animate !== false
        ? 'transform 0.42s cubic-bezier(0.25,0.46,0.45,0.94)'
        : 'none';
      track.style.transform = 'translateX(-' + (currentIndex * getCardWidth()) + 'px)';
      updateDots();
      updateArrows();
    }

    /* ── Arrows ── */
    function updateArrows() {
      if (prevBtn) prevBtn.disabled = currentIndex <= 0;
      if (nextBtn) nextBtn.disabled = currentIndex >= getMaxIndex();
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(currentIndex - 1, true); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(currentIndex + 1, true); });

    /* ── Dots ── */
    function buildDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      var count = getMaxIndex() + 1;
      for (var i = 0; i < count; i++) {
        (function (idx) {
          var dot = document.createElement('button');
          dot.className = 'fc-dot' + (idx === 0 ? ' is-active' : '');
          dot.setAttribute('aria-label', 'Go to slide ' + (idx + 1));
          dot.addEventListener('click', function () { goTo(idx, true); });
          dotsWrap.appendChild(dot);
        })(i);
      }
    }

    function updateDots() {
      if (!dotsWrap) return;
      dotsWrap.querySelectorAll('.fc-dot').forEach(function (d, i) {
        d.classList.toggle('is-active', i === currentIndex);
      });
    }

    buildDots();
    updateArrows();

    /* ── Drag / Swipe ── */
    var dragStartX     = null;
    var dragStartY     = null;
    var isDragging     = false;
    var dragBaseOffset = 0;

    function getComputedOffset() {
      var matrix = window.getComputedStyle(track).transform;
      if (matrix && matrix !== 'none') {
        var m = matrix.match(/matrix.*\((.+)\)/);
        if (m) return parseFloat(m[1].split(', ')[4]) || 0;
      }
      return -(currentIndex * getCardWidth());
    }

    function onDragStart(x, y) {
      dragStartX     = x;
      dragStartY     = y;
      isDragging     = false;
      dragBaseOffset = getComputedOffset();
      track.style.transition = 'none';
      track.style.transform  = 'translateX(' + dragBaseOffset + 'px)';
    }

    function onDragMove(x, y) {
      if (dragStartX === null) return;
      var dx = x - dragStartX;
      var dy = y - dragStartY;
      if (!isDragging) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          dragStartX = null; dragStartY = null;
          return;
        }
        isDragging = true;
      }
      track.style.transform = 'translateX(' + (dragBaseOffset + dx) + 'px)';
    }

    function onDragEnd(x) {
      if (dragStartX === null) return;
      var diff = dragStartX - x;
      if (isDragging && Math.abs(diff) > 40) {
        goTo(currentIndex + (diff > 0 ? 1 : -1), true);
      } else {
        goTo(currentIndex, true);
      }
      dragStartX = null; dragStartY = null; isDragging = false;
    }

    /* Mouse */
    trackWrap.addEventListener('mousedown', function (e) {
      e.preventDefault();
      onDragStart(e.clientX, e.clientY);
    });
    window.addEventListener('mousemove', function (e) {
      if (dragStartX !== null) onDragMove(e.clientX, e.clientY);
    });
    window.addEventListener('mouseup', function (e) {
      if (dragStartX !== null) onDragEnd(e.clientX);
    });

    /* Touch */
    trackWrap.addEventListener('touchstart', function (e) {
      onDragStart(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: true });
    trackWrap.addEventListener('touchmove', function (e) {
      if (dragStartX === null) return;
      if (isDragging) e.preventDefault();
      onDragMove(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: false });
    trackWrap.addEventListener('touchend', function (e) {
      if (dragStartX !== null) onDragEnd(e.changedTouches[0].clientX);
    });

    /* Resize */
    window.addEventListener('resize', function () {
      buildDots();
      goTo(Math.min(currentIndex, getMaxIndex()), false);
    });
  }

  /* ── Init ── */
  function init() {
    document.querySelectorAll('[data-fc-section]').forEach(function (s) {
      initCarousel(s);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector('[data-fc-section]');
    if (!s) return;
    initCarousel(s);
  });

})();